"use client";

import { useLayoutEffect, useState } from "react";

/**
 * Renders an absolute date/time in the **visitor’s local timezone and locale**,
 * after the component mounts. Server-side rendering cannot know the user’s
 * device zone, so the DOM placeholder is a thin space until `useLayoutEffect` runs
 * in the browser (avoids showing raw ISO/UTC in the first paint).
 *
 * For calendar-only strings `YYYY-MM-DD` (e.g. legal "last updated"), set
 * `dateOnly` so the day is interpreted in local calendar date, not UTC midnight.
 */
const PRESETS: Record<string, Intl.DateTimeFormatOptions> = {
  date: {
    month: "short",
    day: "numeric",
    year: "numeric",
  },
  dateTime: {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  },
  long: {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  },
  medium: {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  },
  longDate: {
    year: "numeric",
    month: "long",
    day: "numeric",
  },
  weekdayDateTime: {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  },
  /** e.g. dashboard "Delivery by …" */
  orderDeadline: {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  },
};

function parseToDate(iso: string, dateOnly: boolean | undefined): Date {
  if (dateOnly && /^\d{4}-\d{2}-\d{2}$/.test(iso.trim())) {
    const [y, m, d] = iso.trim().split("-").map(Number);
    if (y > 0 && m >= 1 && m <= 12 && d >= 1 && d <= 31) {
      return new Date(y, m - 1, d, 12, 0, 0);
    }
  }
  return new Date(iso);
}

export type LocalDateTimeProps = {
  /** ISO 8601 instant, or YYYY-MM-DD if `dateOnly` */
  iso: string;
  preset?: keyof typeof PRESETS;
  /** If true, `iso` is treated as a local calendar date (YYYY-MM-DD) */
  dateOnly?: boolean;
  className?: string;
  as?: "span" | "time";
  title?: string;
  /** Shown in SSR + before layout effect (invisible 1ch gap to reduce layout shift) */
  emptyChar?: string;
};

export function LocalDateTime({
  iso,
  preset = "dateTime",
  dateOnly,
  className,
  as: Tag = "span",
  title: titleAttr,
  emptyChar = "\u00a0",
}: LocalDateTimeProps) {
  const [text, setText] = useState("");

  useLayoutEffect(() => {
    const d = parseToDate(iso, dateOnly);
    if (Number.isNaN(d.getTime())) {
      setText("—");
      return;
    }
    const opts = PRESETS[preset] ?? PRESETS.dateTime;
    setText(new Intl.DateTimeFormat(undefined, opts).format(d));
  }, [iso, dateOnly, preset]);

  const show = text || emptyChar;

  let machineTime: string | undefined;
  if (Tag === "time" && text) {
    if (dateOnly && /^\d{4}-\d{2}-\d{2}$/.test(iso.trim())) {
      machineTime = iso.trim();
    } else {
      const d = parseToDate(iso, dateOnly);
      machineTime = Number.isNaN(d.getTime()) ? undefined : d.toISOString();
    }
  }

  return (
    <Tag
      className={className}
      dateTime={machineTime}
      title={titleAttr}
      suppressHydrationWarning
    >
      {show}
    </Tag>
  );
}
